import React, { useState } from "react";
import { useLocation, Link, useParams, useNavigate } from "react-router-dom";
import { enviarComando } from "../services/comandoService.js";

function Valvula() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sectorId } = useParams();

  const sector = location.state?.sector;
  const equipo = location.state?.equipo;

  const [valvulas, setValvulas] = useState(sector?.valvulas ?? []);
  const [notificacion, setNotificacion] = useState(null);

  if (!sector || !equipo) {
    return (
      <div className="p-4">
        No se encontró información del sector o del equipo (sectorId: {sectorId})
      </div>
    );
  }

  const toggleEstadoValvula = async (id) => {
    const valvulaActual = valvulas.find((v) => v.valvulaId === id);
    if (!valvulaActual) return;

    const activaActual =
      valvulaActual.estadoDescripcion.trim().toLowerCase() === "activada";
    const nuevoEstado = activaActual ? "desactivada" : "activada";

    try {
      const comandoEnviar = `m${equipo.moduloId}${valvulaActual.valvulaId}`;
      const respuestaESP = await enviarComando(equipo.ip, comandoEnviar);

      // Actualiza estado local
      const nuevaLista = valvulas.map((v) => {
        if (v.valvulaId === id) {
          return {
            ...v,
            estadoDescripcion:
              nuevoEstado.charAt(0).toUpperCase() + nuevoEstado.slice(1),
          };
        }
        return v;
      });
      setValvulas(nuevaLista);

      setNotificacion({
        mensaje: `ESP8266: ${respuestaESP}`,
        tipo: "success",
      });
    } catch (error) {
      setNotificacion({
        mensaje: `Error al ${nuevoEstado} la válvula "${valvulaActual.descripcion}": ${error.message}`,
        tipo: "error",
      });
    }

    setTimeout(() => setNotificacion(null), 6000);
  };

  return (
    <div
      className="min-h-screen text-gray-800 font-sans relative overflow-x-hidden"
      style={{
        background: "linear-gradient(135deg, #e8ffef 0%, #c3f8ff 100%)"
      }}
    >
      {/* Onda decorativa SVG en la parte inferior */}
      <div className="absolute bottom-0 left-0 w-full pointer-events-none select-none z-0" style={{lineHeight:0}}>
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path fill="#86e2d5" fillOpacity="0.45" d="M0,60 C360,120 1080,0 1440,60 L1440,120 L0,120 Z"></path>
          <path fill="#a8e063" fillOpacity="0.27" d="M0,90 C400,60 1040,140 1440,90 L1440,120 L0,120 Z"></path>
          <path fill="#56c5e1" fillOpacity="0.15" d="M0,110 C480,80 960,160 1440,110 L1440,120 L0,120 Z"></path>
        </svg>
      </div>
      <main className="px-4 md:px-8 pb-16 mt-8 max-w-4xl mx-auto relative z-10">
        <section>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-[#2377b1]">
              Válvulas del sector <span className="font-bold">{sector.sectorNombre}</span>
            </h2>
            <button
              onClick={() => navigate(-1)}
              className="inline-block px-4 py-2 bg-blue-200 text-blue-800 rounded-full font-semibold border border-blue-300 hover:bg-blue-300 hover:text-blue-900 transition"
            >
              ← Volver
            </button>
          </div>

          {/* Lista de válvulas */}
          {valvulas.length === 0 ? (
            <div className="text-gray-500 mb-6">No hay válvulas en este sector.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
              {valvulas.map((valvula) => (
                <div
                  key={valvula.valvulaId}
                  className="relative p-5 rounded-2xl border border-[#dbeafe] bg-white/90 shadow-md hover:shadow-xl hover:ring-2 hover:ring-blue-300 transition-all duration-200 flex flex-col h-full min-h-[170px]"
                >
                  {/* Nombre de la válvula en la esquina superior izquierda */}
                  <div className="absolute top-4 left-4 z-10">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-lg text-sm font-bold shadow">
                      {valvula.codigo || `Válvula ${valvula.valvulaId}`}
                    </span>
                  </div>

                  {/* Gotita centrada en la parte superior */}
                  <div className="w-full flex justify-center absolute left-0 top-3 z-20 pointer-events-none select-none">
                    <span
                      className="text-blue-200"
                      style={{
                        fontSize: "2.7rem",
                        opacity: 0.17,
                        zIndex: 1,
                        lineHeight: "2.7rem",
                      }}
                    >
                      💧
                    </span>
                  </div>

                  {/* Cajita de información */}
                  <div className="relative z-10 flex-1 flex flex-col items-center justify-start mt-12">
                    <div className="w-full bg-gradient-to-b from-green-100 via-blue-50 to-blue-100 border border-blue-100 rounded-xl px-4 py-2 shadow-inner flex flex-col items-center">
                      <p className="text-sm text-gray-500 mb-1">
                        {valvula.descripcion}
                      </p>
                      <span className={`text-xs font-semibold mb-1 ${
                        valvula.estadoDescripcion.trim().toLowerCase() === "activada"
                          ? "text-green-700"
                          : "text-red-600"
                      }`}>
                        Estado: {valvula.estadoDescripcion}
                      </span>
                    </div>
                  </div>

                  {/* Botón de control de la válvula */}
                  <div className="relative z-10 mt-5 flex items-center gap-3 justify-center">
                    <button
                      onClick={() => toggleEstadoValvula(valvula.valvulaId)}
                      className={`text-sm px-4 py-2 rounded-full border-2 font-semibold shadow-sm transition
                        ${
                          valvula.estadoDescripcion.trim().toLowerCase() === "activada"
                            ? "border-red-300 text-red-600 bg-red-100 hover:bg-red-200"
                            : "border-green-300 text-green-700 bg-green-100 hover:bg-green-200"
                        }`}
                    >
                      {valvula.estadoDescripcion.trim().toLowerCase() === "activada" ? "Desactivar" : "Activar"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {notificacion && (
            <div
              className={`fixed bottom-6 right-6 px-5 py-4 rounded shadow-lg z-50 animate-fade-in ${
                notificacion.tipo === "success"
                  ? "bg-green-100 border-l-4 border-green-400 text-green-900"
                  : "bg-red-100 border-l-4 border-red-400 text-red-900"
              }`}
            >
              {notificacion.mensaje}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default Valvula;